import React from 'react';

import { Translate } from 'react-localize-redux';
import Permissions from 'react-redux-permissions';
import { type RouteComponentProps, Route, Switch, withRouter } from 'react-router';

import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../services/featureFlags/useBooleanFeatureFlagResolution';
import LazyComponent from '../../ui/LazyComponent';
import OrganizationRoutes from '../RMS/Organization/Organization.routes';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import SalesChannelsGeneralSettings from '../SalesChannels/Settings/SalesChannelsGeneralSettings';
import AuthorizationRoutes from './Authorization/Authorization.routes';
import AddBrand from './Brands/AddBrand';
import Brands from './Brands/Brands';
import CustomerMessageDetails from './CustomerMessages/CustomerMessageDetails';
import CustomerMessages from './CustomerMessages/CustomerMessages';
import EditTermsAndPolicyData from './Edit/EditTermsAndPolicyData';
import GeneralSettings from './GeneralSettings/GeneralSettings';
import { CreateMetafield } from './Metafields/pages/CreateMetafield';
import { EditMetafield } from './Metafields/pages/EditMetafield';
import { MetafieldDefinitions } from './Metafields/pages/MetafieldDefinitions';
import { MetafieldEntities } from './Metafields/pages/MetafieldEntities';
import PrintingTemplates from './PrintingTemplates/PrintingTemplates';
import AddSalesChannelGroup from './SalesChannelGroups/AddSalesChannelGroup';
import SalesChannelGroupDetails from './SalesChannelGroups/SalesChannelGroupDetails';
import SalesChannelGroups from './SalesChannelGroups/SalesChannelGroups';
import SalesChannelsList from './SalesChannelsSettings/SalesChannelsList';
import WebhooksRoutes from './Webhooks/Webhooks.routes';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

type Props = RouteComponentProps;

const entityRouteParams = 'catalog-item|catalog-group';

const SettingsRoutes: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    match: { path },
  } = props;
  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const useAuthZPermissions = useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value;
  const customerMessagesPermissions = useAuthZPermissions
    ? ['ViewWebsiteContent', 'FlipdishStaff']
    : ['Owner', 'ManagedOwner', 'FlipdishStaff'];

  return (
    <ErrorBoundary identifier="settings">
      <Switch>
        <>
          <Route path={`${path}/organisation`} component={OrganizationRoutes} />

          <Route exact path={`${path}/brands`} component={Brands} />
          <Route exact path={`${path}/brands/add`} component={AddBrand} />
          <Route exact path={`${path}/brand`} component={GeneralSettings} />
          <Route exact path={`${path}/metafields`} component={MetafieldEntities} />
          <Route
            exact
            path={`${path}/metafields/:ownerEntity(${entityRouteParams})`}
            component={MetafieldDefinitions}
          />
          <Route
            exact
            path={`${path}/metafields/:ownerEntity/create`}
            component={CreateMetafield}
          />
          <Route
            exact
            path={`${path}/metafields/:ownerEntity/:definitionKey`}
            component={EditMetafield}
          />

          <Permissions allowed={['ViewSalesChannels', 'EditSalesChannels']}>
            <Route exact path={`${path}/sales-channels`} component={SalesChannelsList} />
            <Route exact path={`${path}/sales-channel-groups`} component={SalesChannelGroups} />
            <Switch>
              <Route
                exact
                path={`${path}/sales-channel-groups/add`}
                component={AddSalesChannelGroup}
              />
              <Route
                exact
                path={`${path}/sales-channel-groups/:salesChannelGroupId`}
                component={SalesChannelGroupDetails}
              />
            </Switch>
          </Permissions>

          <Permissions allowed={customerMessagesPermissions}>
            <Route exact path={`${path}/customer-messages`} component={CustomerMessages} />
            <Route
              exact
              path={`${path}/customer-messages/:salesChannelGroupId`}
              component={CustomerMessageDetails}
            />
          </Permissions>

          <Route
            exact
            path={`${path}/sales-channels/general-settings`}
            component={SalesChannelsGeneralSettings}
          />
          <Route exact path={`${path}/sales-channels/legal`} component={EditTermsAndPolicyData} />

          <Permissions allowed={['ViewDevelopersSettings', 'EditDevelopersSettings']}>
            <Route path={`${path}/webhooks`} component={WebhooksRoutes} />
          </Permissions>

          <Route path={`${path}/teammates`} component={AuthorizationRoutes} />

          <Permissions allowed={['Owner', 'ManagedOwner', 'FlipdishStaff']}>
            <Route
              exact
              path={`${path}/sales-channels/printing-templates`}
              render={() => (
                <LazyComponent>
                  <PrintingTemplates />
                </LazyComponent>
              )}
            />
            <Route
              exact
              path={`${path}/sales-channels/printing-templates/receipt`}
              render={() => (
                <LazyComponent>
                  <RMSPage
                    subscription={RMSSubscriptions.BASE}
                    title={<Translate id="Receipt_Templates" />}
                    module={RMSModules.RECEIPT_TEMPLATES}
                    url={`configurations/system/printingTemplates/${RMSModules.RECEIPT_TEMPLATES}`}
                    includeRMSBrandIdInUrl
                  />
                </LazyComponent>
              )}
            />
            <Route
              exact
              path={`${path}/sales-channels/printing-templates/kitchen-station`}
              render={() => (
                <LazyComponent>
                  <RMSPage
                    subscription={RMSSubscriptions.BASE}
                    title={<Translate id="Kitchen_Station_Templates" />}
                    module={RMSModules.KITCHEN_STATION_TEMPLATES}
                    url={`configurations/system/printingTemplates/${RMSModules.KITCHEN_STATION_TEMPLATES}`}
                    includeRMSBrandIdInUrl
                  />
                </LazyComponent>
              )}
            />
            <Route
              exact
              path={`${path}/sales-channels/printing-templates/voucher`}
              render={() => (
                <LazyComponent>
                  <RMSPage
                    subscription={RMSSubscriptions.BASE}
                    title={<Translate id="Voucher_Templates" />}
                    module={RMSModules.VOUCHER_TEMPLATES}
                    url={`configurations/system/printingTemplates/${RMSModules.VOUCHER_TEMPLATES}`}
                    includeRMSBrandIdInUrl
                  />
                </LazyComponent>
              )}
            />
            <Route
              exact
              path={`${path}/sales-channels/printing-templates/daily-summary-report`}
              render={() => (
                <LazyComponent>
                  <RMSPage
                    subscription={RMSSubscriptions.BASE}
                    title={<Translate id="Daily_Summary_Report_Templates" />}
                    module={RMSModules.DAILY_SUMMARY_REPORT_TEMPLATES}
                    url={`configurations/system/printingTemplates/${RMSModules.DAILY_SUMMARY_REPORT_TEMPLATES}`}
                    includeRMSBrandIdInUrl
                  />
                </LazyComponent>
              )}
            />
          </Permissions>
        </>
      </Switch>
    </ErrorBoundary>
  );
};

export default withRouter(SettingsRoutes);
